import type { DemoScenarioResolved } from 'tio-data/demo/types';
import type {
  DemoClientId,
  DemoSessionRoleId,
  DemoTaPaymentModelId,
} from 'tio-employee/utils/career-pathways-demo-data';

/**
 * App-side demo taxonomy. These types depend on TIO product semantics (and, via
 * the product builders, on TIO models), so they live in the app rather than in
 * tio-data. A {@link DemoScenario} is compiled to a model-agnostic
 * `DemoScenarioResolved` (matchers + fixtures) that the tio-data demo handler
 * consumes.
 *
 * The product config is a discriminated union so impossible combinations (e.g.
 * a TA payment model on a wellness product) cannot be constructed — adding a
 * product forces a matching builder via the exhaustive switch in registry.ts.
 */

export type PslfDemoScenarioId =
  'blank-slate' | 'full-journey' | 'submitted' | 'form-rejected' | 'multi-form';

export type DemoProductConfig =
  | { kind: 'pslf'; scenarioId: PslfDemoScenarioId }
  | { kind: 'tas'; scenarioId: string; taPaymentModelId: DemoTaPaymentModelId }
  | { kind: 'contributions'; scenarioId: string }
  | { kind: 'career-pathways'; pathwayId: string }
  | { kind: 'wellness' }
  | { kind: 'employerMatch' };

export interface DemoScenario {
  persona: string;
  client: DemoClientId;
  role: DemoSessionRoleId;
  products: DemoProductConfig[];
}

/**
 * The display identity every product fixture must agree on. Resolved ONCE from
 * the active selection (client + user scenario) by the registry and threaded
 * through the product builders, so the employer/employee names in product
 * fixtures can never drift from the session identity (`/me`) — the
 * "approval queue says Acme Corporation, header says Lifepoint Health" class
 * of bug. Product fixtures must derive every employer/employee name from this
 * instead of hardcoding one.
 */
export interface DemoFixtureIdentity {
  firstName: string;
  lastName: string;
  /** The selected demo client's company name (e.g. "Lifepoint Health"). */
  employerName: string;
  /** The selected demo client id — drives client-aware fixtures (e.g. the industry-aware TA program catalog). */
  clientId: DemoClientId;
}

/** Signature every per-product builder implements. */
export type ProductBuilder<K extends DemoProductConfig['kind']> = (
  config: Extract<DemoProductConfig, { kind: K }>,
  scenario: DemoScenario,
  identity: DemoFixtureIdentity
) => DemoScenarioResolved;

/** Compile-time exhaustiveness guard for the product-kind switch. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled demo product config: ${JSON.stringify(value)}`);
}
